import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSnackbar } from 'notistack';
import MicRecorder from "mic-recorder-to-mp3";
import { Plus, Mic, FileText, Type, X, Trash2, Check, Loader2 } from 'lucide-react';

// Actions
import actionCadastrarRespostaRapida from 'src/actions/dashboard/respostas-rapidas/actionCadastrarRespostaRapida';
import actionDeletarRespostaRapida from 'src/actions/dashboard/respostas-rapidas/actionDeletarRespostaRapida';
import actionEditarRespostaRapida from 'src/actions/dashboard/respostas-rapidas/actionEditarRespostaRapida';
import actionListaRespostasRapidas from 'src/actions/dashboard/respostas-rapidas/actionListaRespostasRapida';
import v2_actionUploadS3 from 'src/actions/v2/global/actionUploadS3';

// Hooks & Utils
import useAuth from 'src/hooks/useAuth';

// Components
import { UploadUnicoArquivo } from 'src/components/upload';
import ModalConfimacao from 'src/components/modal/ModalConfimacao';
import EmojiPicker from 'src/components/EmojiPicker';
import TableRespostasRapidas from './TableRespostasRapidas';
import CONFIG_PAGE_DESIGN from './RespostasRapidas.design';
import { Input } from 'src/@/components/ui/input';
import { Textarea } from 'src/@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from 'src/@/components/ui/dialog';
import { Separator } from 'src/@/components/ui/separator';

export default function RespostasRapidasComponent() {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  // Dialog & Modal States
  const [openDialog, setOpenDialog] = useState(false);
  const [openModalConfirmacao, setOpenModalConfirmacao] = useState(false);
  const [loading, setLoading] = useState(false);

  // Data States
  const [listaRespostaRapidas, setListasRespostasRapidas] = useState([]);
  const [idRespostaRapida, setIdRespostaRapida] = useState(null);

  // Form States
  const [action, setAction] = useState(''); // 'criar' | 'editar'
  const [tituloEdit, setTituloEdit] = useState('');
  const [respostaEdit, setRespostaEdit] = useState('');
  const [tipoResposta, setTipoResposta] = useState('texto'); // 'texto', 'arquivo', 'audio'
  const [respostaOriginal, setRespostaOriginal] = useState(null);

  // File Upload State
  const [files, setFiles] = useState([]);

  // Audio Recorder State
  const [audioGravado, setAudioGravado] = useState(null);
  const [recording, setRecording] = useState(false);
  const [tempoRestante, setTempoRestante] = useState(180);
  const [mostrarAlerta, setMostrarAlerta] = useState(false);

  // Textarea ref for emoji insertion
  const textareaRef = useRef(null);

  const recorder = useMemo(() => new MicRecorder({ bitRate: 128 }), []);

  // --- Search & Initial Load ---
  const buscarRespostasRapidas = useCallback(async () => {
    setLoading(true);
    try {
      const res = await actionListaRespostasRapidas(user.clienteId);
      setListasRespostasRapidas(res.data.respostas);
    } catch (error) {
      console.error('Erro ao buscar Lista de Respostas rápidas', error);
      enqueueSnackbar('Erro ao buscar respostas rápidas', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [user.clienteId, enqueueSnackbar]);

  useEffect(() => {
    buscarRespostasRapidas();
  }, [buscarRespostasRapidas]);

  // --- Form Handlers ---
  const handleOpenDialog = (actionType = 'criar', data = null) => {
    setAction(actionType);
    if (actionType === 'editar' && data) {
      if (data.tipo !== 'texto') {
        enqueueSnackbar('Não é possível editar resposta rápida do tipo arquivo ou áudio. Exclua e crie uma nova.', { variant: 'warning' });
        return;
      }
      setRespostaOriginal(data);
      setTituloEdit(data.titulo);
      setRespostaEdit(data.resposta);
      setTipoResposta(data.tipo);
    } else {
      resetForm();
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    resetForm();
    pararGravacao(true);
  };

  const resetForm = () => {
    setTituloEdit('');
    setRespostaEdit('');
    setTipoResposta('texto');
    setFiles([]);
    setAudioGravado(null);
    setRecording(false);
    setRespostaOriginal(null);
  };

  // --- File Upload Logic ---
  const handleDropMultiFile = useCallback((acceptedFiles) => {
    setFiles(acceptedFiles.map((file) => Object.assign(file, { preview: URL.createObjectURL(file) })));
  }, []);

  const handleRemoveFile = (file) => {
    const filteredItems = files.filter((_file) => _file !== file);
    setFiles(filteredItems);
  };

  async function uploadAudioAndGetUrl(file) {
    if (file) {
      const formData = new FormData();
      formData.append("file", file);
      try {
        const querys = `?path=plataforma/resposta-rapida`;
        const response = await v2_actionUploadS3(formData, querys);
        if (!response.data.error) {
          return { key: response.data.data.key, type: file.type, tipo: "audio" };
        }
      } catch (error) {
        console.error('Erro ao upload arquivo', error);
      }
    }
    return null;
  }

  // --- Audio Recorder Logic ---
  const handleStartRecording = async () => {
    setTempoRestante(180);
    setMostrarAlerta(false);
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      await recorder.start();
      setRecording(true);

      const timer = setInterval(() => {
        setTempoRestante((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            pararGravacao();
            return 0;
          }
          if (prev <= 30) setMostrarAlerta(true);
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      console.error(err);
      enqueueSnackbar('Erro ao iniciar gravação. Verifique as permissões.', { variant: 'error' });
    }
  };

  const pararGravacao = async (discard = false) => {
    try {
      if (recorder) {
        const [buffer, blob] = await recorder.stop().getMp3();
        if (!discard) {
          const file = new File(buffer, "audio.mp3", {
            type: blob.type,
            lastModified: Date.now()
          });
          setAudioGravado(file);
        }
        setRecording(false);
      }
    } catch (error) {
      console.error(error);
      setRecording(false);
    }
  };

  // --- Save / Update Logic ---
  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (action === 'editar') {
        const data = {
          id: respostaOriginal._id,
          titulo: tituloEdit,
          resposta: respostaEdit,
        };
        await actionEditarRespostaRapida(data);
        enqueueSnackbar('Resposta rápida atualizada!', { variant: 'success' });
      } else {
        let filesUrl = [];
        if (tipoResposta === 'audio' && audioGravado) {
          const uploaded = await uploadAudioAndGetUrl(audioGravado);
          if (uploaded) filesUrl.push(uploaded);
        } else if (tipoResposta === 'arquivo' && files.length > 0) {
          const uploaded = await uploadAudioAndGetUrl(files[0]);
          if (uploaded) {
            uploaded.tipo = "arquivo";
            filesUrl.push(uploaded);
          }
        }

        const data = {
          clienteId: user.clienteId,
          titulo: tituloEdit,
          respostaField: respostaEdit,
          tipo: tipoResposta,
          files: filesUrl
        };
        await actionCadastrarRespostaRapida(data);
        enqueueSnackbar('Resposta rápida criada!', { variant: 'success' });
      }

      handleCloseDialog();
      buscarRespostasRapidas();
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Erro ao salvar resposta rápida.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await actionDeletarRespostaRapida({ id: idRespostaRapida });
      enqueueSnackbar('Resposta removida com sucesso.', { variant: 'info' });
      await buscarRespostasRapidas();
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Erro ao deletar.', { variant: 'error' });
    } finally {
      setLoading(false);
      setOpenModalConfirmacao(false);
      setIdRespostaRapida(null);
    }
  };

  // --- Validation ---
  const isFormValid = () => {
    if (!tituloEdit) return false;
    if (tipoResposta === 'texto' && !respostaEdit) return false;
    if (tipoResposta === 'arquivo' && files.length === 0) return false;
    if (tipoResposta === 'audio' && !audioGravado) return false;
    return true;
  };

  return (
    <div className={CONFIG_PAGE_DESIGN.page.container}>
      {/* Header */}
      <div className={CONFIG_PAGE_DESIGN.page.header.container}>
        <div>
          <h1 className={CONFIG_PAGE_DESIGN.page.header.title}>Respostas Rápidas</h1>
          <p className={CONFIG_PAGE_DESIGN.page.header.subtitle}>
            Cadastre modelos de mensagens (texto, arquivo ou áudio) para agilizar o atendimento.
          </p>
        </div>
        <button
          onClick={() => handleOpenDialog('criar')}
          className={CONFIG_PAGE_DESIGN.page.header.actionButton}
        >
          <Plus size={18} />
          <span>Nova Resposta</span>
        </button>
      </div>

      {/* Content */}
      <div className="flex flex-col gap-4">
        {loading && listaRespostaRapidas.length === 0 ? (
          <div className="w-full h-1 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full w-1/3 bg-[#7C3AED] rounded-full animate-[pulse_1.5s_ease-in-out_infinite]" />
          </div>
        ) : (
          <TableRespostasRapidas
            data={listaRespostaRapidas}
            onEdit={(row) => handleOpenDialog('editar', row)}
            onDelete={(row) => {
              setIdRespostaRapida(row._id);
              setOpenModalConfirmacao(true);
            }}
          />
        )}
      </div>

      {/* Confirmation Modal */}
      <ModalConfimacao
        open={openModalConfirmacao}
        onClose={() => setOpenModalConfirmacao(false)}
        onConfirm={handleDelete}
        loadingFinalizar={loading}
        mensagem={'Deseja realmente excluir esta resposta rápida? Esta ação não pode ser desfeita.'}
      />

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onOpenChange={(open) => { if (!open) handleCloseDialog(); }}>
        <DialogContent className="max-w-[600px] p-0 gap-0 rounded-2xl">
          <DialogHeader className="px-6 pt-5 pb-4">
            <DialogTitle className="text-xl font-bold text-gray-900">
              {action === 'editar' ? 'Editar Resposta Rápida' : 'Nova Resposta Rápida'}
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-500 mt-1">
              {action === 'editar'
                ? 'Altere o título ou conteúdo da resposta rápida.'
                : 'Crie uma resposta rápida para agilizar seus atendimentos.'
              }
            </DialogDescription>
          </DialogHeader>

          <Separator className="bg-gray-100" />

          <div className="px-6 py-5 flex flex-col gap-5 max-h-[60vh] overflow-y-auto">
            {/* Titulo */}
            <div>
              <label className={CONFIG_PAGE_DESIGN.dialog.section.label}>Nome / Identificação</label>
              <Input
                value={tituloEdit}
                onChange={(e) => setTituloEdit(e.target.value)}
                placeholder="Ex: Saudação Inicial"
                className={CONFIG_PAGE_DESIGN.dialog.section.input}
              />
            </div>

            {/* Tipo Selector */}
            <div>
              <label className={CONFIG_PAGE_DESIGN.dialog.section.label}>Tipo da Mensagem</label>
              <div className="flex gap-3">
                {[
                  { id: 'texto', label: 'Texto', icon: <Type size={18} /> },
                  { id: 'arquivo', label: 'Arquivo', icon: <FileText size={18} /> },
                  { id: 'audio', label: 'Áudio', icon: <Mic size={18} /> }
                ].map((type) => (
                  <button
                    key={type.id}
                    onClick={() => setTipoResposta(type.id)}
                    disabled={action === 'editar'}
                    className={`
                      flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border transition-all
                      ${tipoResposta === type.id
                        ? 'border-[#7C3AED] bg-[#7C3AED]/5 text-[#7C3AED] font-medium'
                        : 'border-gray-200 hover:border-gray-300 text-gray-600'
                      }
                      ${action === 'editar' ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                    `}
                  >
                    {type.icon}
                    <span>{type.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Dynamic Content Inputs */}
            <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 min-h-[150px]">
              {tipoResposta === 'texto' && (
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700">Conteúdo da Mensagem</label>
                    <EmojiPicker
                      setValue={setRespostaEdit}
                      alignRight
                    />
                  </div>
                  <Textarea
                    ref={textareaRef}
                    rows={5}
                    value={respostaEdit}
                    onChange={(e) => setRespostaEdit(e.target.value)}
                    placeholder="Digite sua mensagem aqui..."
                    className="bg-white"
                  />
                </div>
              )}

              {tipoResposta === 'arquivo' && (
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-gray-700">Anexar Arquivo</label>
                  <UploadUnicoArquivo
                    files={files}
                    onDrop={handleDropMultiFile}
                    onRemove={handleRemoveFile}
                    showPreview={false}
                    isEdit={false}
                  />
                </div>
              )}

              {tipoResposta === 'audio' && (
                <div className="flex flex-col items-center justify-center py-6 gap-4">
                  {!audioGravado && !recording && (
                    <button
                      onClick={handleStartRecording}
                      className="flex flex-col items-center gap-2 text-gray-500 hover:text-[#7C3AED] transition-colors cursor-pointer"
                    >
                      <div className="w-16 h-16 rounded-full bg-white border-2 border-dashed border-gray-300 flex items-center justify-center hover:border-[#7C3AED] transition-colors">
                        <Mic size={32} />
                      </div>
                      <span className="text-sm font-medium">Clique para começar a gravar</span>
                    </button>
                  )}

                  {recording && (
                    <div className="flex flex-col items-center gap-4 animate-in fade-in">
                      <span className="text-red-500 font-semibold flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                        Gravando... {Math.floor(tempoRestante / 60)}:{(tempoRestante % 60).toString().padStart(2, '0')}
                      </span>
                      <div className="flex gap-4">
                        <button
                          onClick={() => pararGravacao(true)}
                          className="w-10 h-10 rounded-full bg-red-50 hover:bg-red-100 flex items-center justify-center text-red-500 transition-colors cursor-pointer"
                        >
                          <X size={20} />
                        </button>
                        <button
                          onClick={() => pararGravacao(false)}
                          className="w-10 h-10 rounded-full bg-green-50 hover:bg-green-100 flex items-center justify-center text-green-600 transition-colors cursor-pointer"
                        >
                          <Check size={20} />
                        </button>
                      </div>
                    </div>
                  )}

                  {audioGravado && (
                    <div className="w-full bg-white p-3 rounded-lg border border-gray-200 flex items-center justify-between gap-3">
                      <audio controls className="w-full h-8">
                        <source src={URL.createObjectURL(audioGravado)} type="audio/mp3" />
                        Seu navegador não suporta áudio.
                      </audio>
                      <button
                        onClick={() => setAudioGravado(null)}
                        className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <Separator className="bg-gray-100" />

          <DialogFooter className="px-6 py-4 bg-gray-50/80 rounded-b-2xl sm:justify-end gap-3">
            <button
              onClick={handleCloseDialog}
              className={CONFIG_PAGE_DESIGN.dialog.actions.cancelButton}
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={!isFormValid() || loading}
              className={`
                ${CONFIG_PAGE_DESIGN.dialog.actions.confirmButton.base}
                ${!isFormValid() || loading
                  ? CONFIG_PAGE_DESIGN.dialog.actions.confirmButton.disabled
                  : CONFIG_PAGE_DESIGN.dialog.actions.confirmButton.active
                }
              `}
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              {action === 'editar' ? 'Salvar Alterações' : 'Criar Resposta'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
